const toDisplayDays = (days) => {
    const numericDays = Number(days);
    return Number.isFinite(numericDays) ? numericDays : days;
};

export const LEAVE_POLICY_CATEGORIES = Object.freeze({
    PRIMARY: 'primary',
    DERIVED: 'derived',
    MANUAL: 'manual'
});

export const LEAVE_POLICY_CHANGE_MODES = Object.freeze({
    POLICY_VERSION: 'policy_version',
    DIRECT_EDIT: 'direct_edit',
    DERIVED_RULE: 'derived_rule',
    MANUAL_GRANT: 'manual_grant'
});

export const isPrimaryPolicyLeaveType = (leaveType) => (
    leaveType?.policy_category === LEAVE_POLICY_CATEGORIES.PRIMARY
);

export const isPolicyVersionManagedLeaveType = (leaveType) => (
    leaveType?.policy_change_mode === LEAVE_POLICY_CHANGE_MODES.POLICY_VERSION
);

export const isPolicyVersionSelectableLeaveType = (leaveType) => (
    isPrimaryPolicyLeaveType(leaveType)
    && leaveType?.is_policy_version_selectable !== false
    && (
        leaveType?.is_active === true
        || leaveType?.is_active === 1
        || isPolicyVersionManagedLeaveType(leaveType)
    )
);

export const getLeavePolicyStatusLabel = (leaveType) => {
    if (leaveType?.has_policy_history) return 'Versioned';
    if (isPolicyVersionManagedLeaveType(leaveType)) return 'Base default';
    return 'Editable';
};

export const parseLeaveTypeAttributes = (attributes) => {
    if (!attributes) return {};
    if (typeof attributes !== 'string') return attributes;

    try {
        return JSON.parse(attributes);
    } catch (_error) {
        return {};
    }
};

export const normalizePolicyCodeList = (value) => {
    if (!Array.isArray(value)) return [];
    return [...new Set(value
        .map((item) => String(item || '').trim().toUpperCase())
        .filter(Boolean))];
};

const toPolicyDateKey = (value) => {
    if (!value) return '';
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return String(value).slice(0, 10);
};

export const findLatestPolicyRuleForLeaveType = (policyVersions = [], leaveTypeId) => {
    const normalizedLeaveTypeId = Number(leaveTypeId);
    if (!Number.isInteger(normalizedLeaveTypeId) || normalizedLeaveTypeId <= 0) return null;

    const orderedVersions = [...policyVersions]
        .filter((version) => version?.status === 'active')
        .sort((left, right) => {
            const joinerOrder = toPolicyDateKey(right.joiner_from)
                .localeCompare(toPolicyDateKey(left.joiner_from));
            if (joinerOrder !== 0) return joinerOrder;

            const effectiveOrder = toPolicyDateKey(right.effective_from)
                .localeCompare(toPolicyDateKey(left.effective_from));
            if (effectiveOrder !== 0) return effectiveOrder;

            return Number(right.id || 0) - Number(left.id || 0);
        });

    for (const version of orderedVersions) {
        const rule = (version.leave_policy_rule || version.rules || []).find((candidate) => (
            Number(candidate.leave_type_id) === normalizedLeaveTypeId
        ));
        if (rule) return rule;
    }

    return null;
};

export const getLeavePolicyRuleDefaults = (leaveType = {}, policyRule = null) => {
    const hasRuleValue = (key) => policyRule
        && Object.prototype.hasOwnProperty.call(policyRule, key);
    const attributes = parseLeaveTypeAttributes(
        hasRuleValue('attributes') ? policyRule.attributes : leaveType.attributes
    );
    const eligibility = attributes.eligibility || {};
    const days = hasRuleValue('days') ? policyRule.days : leaveType.days;
    const enabled = hasRuleValue('is_enabled')
        ? policyRule.is_enabled !== false && policyRule.is_enabled !== 0
        : leaveType.is_active === true || leaveType.is_active === 1;
    const description = hasRuleValue('description')
        ? policyRule.description
        : leaveType.description;

    return {
        leave_type_id: leaveType.id,
        days: days === null || days === undefined
            ? null
            : Number(days),
        is_enabled: enabled,
        eligibility_gender: eligibility.gender || undefined,
        eligibility_nationality: normalizePolicyCodeList(eligibility.nationality),
        eligibility_nationality_exclude: normalizePolicyCodeList(eligibility.nationality_exclude),
        description: description || undefined
    };
};

export const buildLeavePolicyRuleAttributes = (leaveType = {}, rule = {}) => {
    const attributes = {
        ...parseLeaveTypeAttributes(leaveType.attributes)
    };
    // Unpaid days are determined by request allocation. Do not copy the
    // obsolete leave-type flag into newly published policy snapshots.
    delete attributes.unpaid;
    delete attributes.supportingDocument;
    if (!attributes.label) attributes.label = leaveType.policy_label || leaveType.leave_name;

    const eligibility = {
        ...(attributes.eligibility || {})
    };
    const nationality = normalizePolicyCodeList(rule.eligibility_nationality);
    const nationalityExclude = normalizePolicyCodeList(rule.eligibility_nationality_exclude);

    if (rule.eligibility_gender) eligibility.gender = rule.eligibility_gender;
    else delete eligibility.gender;

    if (nationality.length > 0) eligibility.nationality = nationality;
    else delete eligibility.nationality;

    if (nationalityExclude.length > 0) eligibility.nationality_exclude = nationalityExclude;
    else delete eligibility.nationality_exclude;

    if (Object.keys(eligibility).length > 0) attributes.eligibility = eligibility;
    else delete attributes.eligibility;

    return attributes;
};

export const formatPolicyRuleSummary = (policy = {}) => (
    (policy.leave_policy_rule || policy.rules || [])
        .map((rule) => {
            const label = rule.leave_name || 'Leave';
            return rule.is_enabled === false
                ? `${label}: unavailable`
                : `${label}: ${toDisplayDays(rule.days)} days`;
        })
        .join(', ')
);
